import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FaBook } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../Authentication/AuthContext';
import {
  getBookList,
  getBookNameByLanguage,
  getTestamentConfig,
  normalizeBookKey,
} from './BookNames';
import {
  getBookChapterCount,
  getChapterVerses,
  prefetchWholeBookInBackground,
  startBibleDownloadCache,
} from './DownloadCache';
import './Bible.css';

const getBibleTitle = (language) => {
  if (language === 'English') {
    return 'Holy Bible';
  }

  if (language === 'Deutsch') {
    return 'Die Bibel';
  }

  return 'பரிசுத்த வேதாகமம்';
};

function Bible() {
  const navigate = useNavigate();
  const { language } = useContext(AuthContext);

  const oldConfig = useMemo(() => getTestamentConfig('old'), []);
  const newConfig = useMemo(() => getTestamentConfig('new'), []);

  const oldBooks = useMemo(() => getBookList('old'), []);
  const newBooks = useMemo(() => getBookList('new'), []);

  const [expandedBookId, setExpandedBookId] = useState('');
  const [openingChapterId, setOpeningChapterId] = useState('');

  const bibleTitle = getBibleTitle(language);
  const oldLabel = getBookNameByLanguage(oldConfig.titleKey, language);
  const newLabel = getBookNameByLanguage(newConfig.titleKey, language);

  useEffect(() => {
    document.body.classList.add('bible-background');

    startBibleDownloadCache();

    return () => {
      document.body.classList.remove('bible-background');
    };
  }, []);

  useEffect(() => {
    const updateBibleViewportVariables = () => {
      const visualViewport = window.visualViewport;
      const visualViewportHeight =
        visualViewport && visualViewport.height
          ? visualViewport.height
          : window.innerHeight;

      const bottomBrowserSpace =
        visualViewport && visualViewport.height
          ? Math.max(
              0,
              window.innerHeight -
                visualViewport.height -
                visualViewport.offsetTop
            )
          : 0;

      document.documentElement.style.setProperty(
        '--bible-visual-viewport-height',
        `${Math.round(visualViewportHeight)}px`
      );

      document.documentElement.style.setProperty(
        '--bible-mobile-bottom-space',
        `${Math.round(bottomBrowserSpace)}px`
      );
    };

    updateBibleViewportVariables();

    window.addEventListener('resize', updateBibleViewportVariables);
    window.addEventListener('orientationchange', updateBibleViewportVariables);

    if (window.visualViewport) {
      window.visualViewport.addEventListener(
        'resize',
        updateBibleViewportVariables
      );
      window.visualViewport.addEventListener(
        'scroll',
        updateBibleViewportVariables
      );
    }

    return () => {
      window.removeEventListener('resize', updateBibleViewportVariables);
      window.removeEventListener(
        'orientationchange',
        updateBibleViewportVariables
      );

      if (window.visualViewport) {
        window.visualViewport.removeEventListener(
          'resize',
          updateBibleViewportVariables
        );
        window.visualViewport.removeEventListener(
          'scroll',
          updateBibleViewportVariables
        );
      }

      document.documentElement.style.removeProperty(
        '--bible-visual-viewport-height'
      );
      document.documentElement.style.removeProperty(
        '--bible-mobile-bottom-space'
      );
    };
  }, []);

  useEffect(() => {
    const stopHeaderTouchDrag = (event) => {
      if (!event.touches || event.touches.length !== 1) {
        return;
      }

      event.preventDefault();
    };

    const headerElements = [
      document.querySelector('.top-header-shell'),
      document.querySelector('.top-header'),
      document.querySelector('.contact-info'),
    ].filter(Boolean);

    headerElements.forEach((headerElement) => {
      headerElement.addEventListener('touchmove', stopHeaderTouchDrag, {
        passive: false,
      });
    });

    return () => {
      headerElements.forEach((headerElement) => {
        headerElement.removeEventListener('touchmove', stopHeaderTouchDrag);
      });
    };
  }, []);

  const getExpandedBookId = useCallback((testamentType, bookKey) => {
    return `${testamentType}:${bookKey}`;
  }, []);

  const getChapterPath = useCallback((config, bookKey, chapterNumber) => {
    return `/${config.routeSegment}/chapters/${bookKey}/${chapterNumber}`;
  }, []);

  const prefetchBook = useCallback((bookName, testamentType) => {
    const bookKey = normalizeBookKey(bookName);

    prefetchWholeBookInBackground(testamentType, bookKey);
  }, []);

  const prefetchChapter = useCallback(
    async (testamentType, bookKey, chapterNumber) => {
      if (!testamentType || !bookKey || !chapterNumber) {
        return [];
      }

      const loadedVerses = await getChapterVerses(
        testamentType,
        bookKey,
        chapterNumber
      );

      return Array.isArray(loadedVerses) ? loadedVerses : [];
    },
    []
  );

  const handleBookClick = useCallback(
    (bookName, testamentType) => {
      const bookKey = normalizeBookKey(bookName);
      const nextExpandedBookId = getExpandedBookId(testamentType, bookKey);

      prefetchWholeBookInBackground(testamentType, bookKey);

      setExpandedBookId((previousValue) =>
        previousValue === nextExpandedBookId ? '' : nextExpandedBookId
      );
    },
    [getExpandedBookId]
  );

  const handleChapterClick = useCallback(
    async (testamentType, bookKey, chapterNumber, config) => {
      const nextOpeningChapterId = `${testamentType}:${bookKey}:${chapterNumber}`;

      if (openingChapterId) {
        return;
      }

      setOpeningChapterId(nextOpeningChapterId);

      const loadedVerses = await prefetchChapter(
        testamentType,
        bookKey,
        chapterNumber
      );

      setOpeningChapterId('');

      if (loadedVerses && loadedVerses.length > 0) {
        navigate(getChapterPath(config, bookKey, chapterNumber), {
          state: {
            prefetchedVerses: loadedVerses,
            prefetchedBook: bookKey,
            prefetchedChapter: chapterNumber,
            prefetchedTestamentType: testamentType,
          },
        });

        return;
      }

      navigate(getChapterPath(config, bookKey, chapterNumber));
    },
    [getChapterPath, navigate, openingChapterId, prefetchChapter]
  );

  const renderChaptersDropdown = useCallback(
    (bookName, testamentType, config) => {
      const bookKey = normalizeBookKey(bookName);
      const chapterCount = getBookChapterCount(testamentType, bookKey);

      if (!chapterCount) {
        return null;
      }

      const chapters = Array.from(
        { length: chapterCount },
        (_, index) => index + 1
      );

      const hasManyChapters = chapterCount > 100;

      return (
        <div
          className={`bible-book-chapters-dropdown ${
            hasManyChapters ? 'bible-book-chapters-dropdown-scroll' : ''
          }`}
        >
          {chapters.map((chapterNumber) => {
            const chapterOpeningId = `${testamentType}:${bookKey}:${chapterNumber}`;
            const isOpening = openingChapterId === chapterOpeningId;

            return (
              <button
                key={`${bookKey}-${chapterNumber}`}
                type="button"
                className={`bible-book-chapter-square ${
                  isOpening ? 'bible-book-chapter-square-opening' : ''
                }`}
                onClick={() =>
                  handleChapterClick(
                    testamentType,
                    bookKey,
                    chapterNumber,
                    config
                  )
                }
                onMouseEnter={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                onFocus={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                onTouchStart={() =>
                  prefetchChapter(testamentType, bookKey, chapterNumber)
                }
                disabled={Boolean(openingChapterId)}
              >
                {chapterNumber}
              </button>
            );
          })}
        </div>
      );
    },
    [handleChapterClick, openingChapterId, prefetchChapter]
  );

  const renderBook = useCallback(
    (bookName, index, testamentType, config) => {
      const bookKey = normalizeBookKey(bookName);
      const currentExpandedBookId = getExpandedBookId(testamentType, bookKey);
      const isExpanded = expandedBookId === currentExpandedBookId;

      return (
        <div
          key={bookName}
          className={`bible-book-item ${
            isExpanded ? 'bible-book-item-expanded' : ''
          }`}
        >
          <button
            type="button"
            className={`bible-book-tile bible-book-${testamentType}`}
            onClick={() => handleBookClick(bookName, testamentType)}
            onMouseEnter={() => prefetchBook(bookName, testamentType)}
            onFocus={() => prefetchBook(bookName, testamentType)}
            onTouchStart={() => prefetchBook(bookName, testamentType)}
            aria-expanded={isExpanded}
          >
            <span className="bible-book-index">{index + 1}</span>

            <span className="bible-book-name">
              {getBookNameByLanguage(bookName, language)}
            </span>

            <span
              className={`bible-book-dropdown-arrow ${
                isExpanded ? 'bible-book-dropdown-arrow-open' : ''
              }`}
              aria-hidden="true"
            >
              <svg
                className="bible-book-dropdown-arrow-icon"
                viewBox="0 0 24 24"
                focusable="false"
              >
                <path d="M7 10l5 5 5-5" />
              </svg>
            </span>
          </button>

          {isExpanded
            ? renderChaptersDropdown(bookName, testamentType, config)
            : null}
        </div>
      );
    },
    [
      expandedBookId,
      getExpandedBookId,
      handleBookClick,
      language,
      prefetchBook,
      renderChaptersDropdown,
    ]
  );

  return (
    <main className="bible-page">
      <section className="bible-home-panel">
        <header className="bible-home-header">
          <div className="bible-home-title-row">
            <FaBook className="bible-home-header-icon" />
            <h1 className="bible-home-title">{bibleTitle}</h1>
          </div>
        </header>

        <div className="bible-home-testaments">
          <section className="bible-testament-col bible-testament-old-col">
            <div className="bible-testament-col-head">
              <span className="bible-testament-dot bible-dot-old" />
              <span className="bible-testament-label">{oldLabel}</span>
              <span className="bible-testament-count">{oldBooks.length}</span>
            </div>

            <div className="bible-testament-list">
              {oldBooks.map((bookName, index) =>
                renderBook(bookName, index, 'old', oldConfig)
              )}
            </div>
          </section>

          <section className="bible-testament-col bible-testament-new-col">
            <div className="bible-testament-col-head">
              <span className="bible-testament-dot bible-dot-new" />
              <span className="bible-testament-label">{newLabel}</span>
              <span className="bible-testament-count">{newBooks.length}</span>
            </div>

            <div className="bible-testament-list">
              {newBooks.map((bookName, index) =>
                renderBook(bookName, index, 'new', newConfig)
              )}
            </div>
          </section>
        </div>
      </section>
    </main>
  );
}

export default Bible;